import React, { useEffect, useState, useRef } from "react";
import { useParams, useLocation } from "react-router-dom";
import { gsap } from "gsap";
import ProductDescription from "./ProductDescription";
import WhyChooseUs from "./WhyChooseUs";
import ringsData from "../data/updated_products.json";
import necklacesData from "../data/necklaces_products.json";
import braceletsData from "../data/bracelets_products.json";
import earringsData from "../data/earring_products.json";
import pandentsData from "../data/pandent_products.json"
import "./ProductDetail.css";

const ProductDetail = () => {
  const { id } = useParams();
  const location = useLocation();
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [mediaList, setMediaList] = useState([]);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [isVideo, setIsVideo] = useState(false);
  const mediaRef = useRef(null);

  const isValidMedia = (media) => media && media.trim() !== "";
   // Determine category from URL path (Assumes path is `/category/:id`)
   const category = location.pathname.split("/")[1];

   // Function to select correct data file based on category
   const getProductsByCategory = () => {
     switch (category) {
       case "rings":
         return ringsData;
       case "necklaces":
         return necklacesData;
       case "bracelets":
         return braceletsData;
       case "earrings":
         return earringsData;
         case "pandent":
         return pandentsData;
       default:
         return [];
     }
   };

   useEffect(() => {
    const products = getProductsByCategory();
    // Find the product by ID within the selected category
    const product = products.find((product) => product.id === parseInt(id, 10));

    if (product) {
      setSelectedProduct(product);
      const combinedMedia = [
        ...(product.images || []).filter((media) => media.trim() !== ""),
        ...(product?.videos || []).filter((media) => media.trim() !== ""),
      ];
      setMediaList(combinedMedia);

      if (combinedMedia.length > 0) {
        setSelectedMedia(combinedMedia[0]);
        setIsVideo(combinedMedia[0].endsWith(".mp4"));
      }
    }
  }, [id, category]);


  const handleMouseMove = (e) => {
    if (isVideo) return;
    const rect = e.target.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const percentX = (x / rect.width) * 100;
    const percentY = (y / rect.height) * 100;

    gsap.to(mediaRef.current, {
      transformOrigin: `${percentX}% ${percentY}%`,
      duration: 0.1,
    });
  };

  const handleMouseEnter = () => {
    if (!isVideo) {
      gsap.to(mediaRef.current, { scale: 2, duration: 0.3 });
    }
  };

  const handleMouseLeave = () => {
    if (!isVideo) {
      gsap.to(mediaRef.current, { scale: 1, duration: 0.3 });
    }
  };

  const handleMediaClick = (media) => {
    setSelectedMedia(media);
    setIsVideo(media.endsWith(".mp4"));
  };

  const handleInquiryClick = () => {
    const currentUrl = window.location.href;
    const message = `Hello, I am interested in this product: ${selectedProduct.name}. Here is the link: ${currentUrl}`;
    const whatsappUrl = `https://wa.me/9426374716?text=${encodeURIComponent(
      message
    )}`;
    window.open(whatsappUrl, "_blank");
  };

  if (!selectedProduct) {
    return <div className="text-center py-10 text-lg font-semibold">Loading...</div>;
  }

  return (
    <div className="product-detail bg-gray-100 py-10">
      <div className="container mx-auto p-6 flex flex-col lg:flex-row gap-8">
        {/* Left Section - Media Display */}
        {mediaList.length > 0 && (
          <div className="media-container flex flex-col items-center w-full lg:w-1/2">
            <div
              className="relative w-full max-w-md h-96 border border-gray-300 rounded-xl overflow-hidden shadow-lg cursor-pointer bg-white"
              onMouseMove={handleMouseMove}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              {isVideo ? (
                <video
                  ref={mediaRef}
                  src={selectedMedia}
                  className="w-full h-full object-cover"
                  autoPlay
                  loop
                  muted
                  playsInline
                />
              ) : (
                <img
                  ref={mediaRef}
                  src={selectedMedia}
                  alt="Selected"
                  className="w-full h-full object-cover"
                />
              )}
            </div>
            <div className="w-full overflow-x-auto whitespace-nowrap scrollbar-hidden">
  <div className="inline-flex gap-2 mt-4 flex-nowrap">
    {mediaList.map((media, index) => (
      <div
        key={index}
        className={`relative w-16 h-16 sm:w-20 sm:h-20 border rounded-lg cursor-pointer transition-all duration-300 shrink-0 ${
          media === selectedMedia ? "border-blue-500 shadow-lg" : "border-gray-300"
        }`}
        onClick={() => handleMediaClick(media)}
      >
        {media.endsWith(".mp4") ? (
          <video
            src={media}
            className="w-full h-full object-cover rounded-md"
            muted
            playsInline
            onError={(e) => (e.target.style.display = "none")}
          />
        ) : (
          <img
            src={media}
            alt={`Thumbnail ${index}`}
            className="w-full h-full object-cover rounded-md"
            onError={(e) => (e.target.style.display = "none")}
          />
        )}
      </div>
    ))}
  </div>
</div>

          </div>
        )}

        {/* Right Section - Product Info */}
        <div className="info-container w-full lg:w-1/2 text-center lg:text-left bg-white p-6 rounded-xl shadow-lg">
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-800 mb-4">
            {selectedProduct.name}
          </h1>
          <p className="text-lg text-gray-600 mb-4">{selectedProduct.description}</p>

          {/* Additional Product Details */}
          <div className="bg-gray-200 p-4 rounded-lg text-gray-700 mb-6">
            <p className="text-lg font-semibold">Product ID: <span className="text-blue-600">{selectedProduct.newCode}</span></p>
            <p className="text-lg font-semibold">
              Total Diamond Weight:{" "}
              <span className="text-green-600">
                {selectedProduct.details.diamondDetails[0].totalDiamondWeight} CT
              </span>
            </p>
          </div>
          
           {/* Size Dropdown */}
           { selectedProduct?.sizes &&(
            <div className="mt-4">
              <label className="text-lg font-semibold block mb-2">Select Size:</label>
              <select className="w-full p-2 border rounded-lg">
                {selectedProduct?.sizes?.map((size, index) => (
                  <option key={index} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </div>
          
         )}

     {/* Metal Tone Dropdown */}
     <div className="mt-4">
      <label className="text-lg font-semibold block mb-2">Select Metal Tone:</label>
      <select className="w-full p-2 border rounded-lg">
        {selectedProduct.metalTone.map((tone, index) => (
          <option key={index} value={tone}>
            {tone}
          </option>
        ))}
      </select>
    </div>


     {/* Materials Dropdown */}
     <div className="mt-4">
      <label className="text-lg font-semibold block mb-2">Select Material:</label>
      <select className="w-full p-2 border rounded-lg">
        {selectedProduct.materials.map((material, index) => (
          <option key={index} value={material}>
            {material}
          </option>
        ))}
      </select>
  

          {/* Inquire Us Button */}
          <button
            onClick={handleInquiryClick}
            className="bg-green-600 text-white px-8 py-3 rounded-lg shadow-lg text-lg font-semibold transition-all duration-300 hover:scale-105 mt-4"
          >
            Inquire Now
          </button>
        </div>
      </div>
</div>
      <ProductDescription details={selectedProduct} />
      <WhyChooseUs />
    </div>
  );
};

export default ProductDetail;
